/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setAlert } from '../../features/slices/alertSlice';
import { saveServiceMaster } from '../../features/slices/serviceMasterSlice';
import { AppDispatch, RootState } from '../../features/store';
import HCInput from '../Input';
import HCModal from '../Modal';

interface AddFacilityServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Called with the new master service Id after a successful save */
    onCreated: (serviceId: number) => void;
}

const AddFacilityServiceModal = ({
    isOpen,
    onClose,
    onCreated,
}: AddFacilityServiceModalProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { data: user } = useSelector((state: RootState) => state.auth);
    const [serviceArea, setServiceArea] = useState('');
    const [serviceSubArea, setServiceSubArea] = useState('');
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setServiceArea('');
        setServiceSubArea('');
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSave = () => {
        if (!serviceArea.trim() || !serviceSubArea.trim()) {
            dispatch(
                setAlert({
                    message: 'Service Area and Service Sub Area are required',
                    severity: 'error',
                }),
            );
            return;
        }
        setSaving(true);
        dispatch(
            saveServiceMaster({
                id: 0,
                serviceArea: serviceArea.trim(),
                serviceSubArea: serviceSubArea.trim(),
                createdBy: user?.UserID,
            }),
        ).then((res: any) => {
            setSaving(false);
            const payload = res?.payload;
            if (
                res?.meta?.requestStatus === 'fulfilled' &&
                payload?.responseCode === '200'
            ) {
                const newId =
                    payload?.result?.id ??
                    payload?.result?.Id ??
                    payload?.result;
                const serviceId = Number(newId);
                if (!serviceId || Number.isNaN(serviceId)) {
                    dispatch(
                        setAlert({
                            message: 'Service saved but id was not returned',
                            severity: 'error',
                        }),
                    );
                    return;
                }
                dispatch(
                    setAlert({
                        message: 'Service added successfully',
                        severity: 'success',
                    }),
                );
                reset();
                onClose();
                onCreated(serviceId);
                return;
            }
            // Prefer API message (e.g. duplicate). Axios interceptor may also alert.
            const raw =
                payload?.responseMessage ||
                payload?.response?.data?.responseMessage ||
                '';
            const message = /already exists/i.test(String(raw))
                ? 'This service already exists in the global catalog. Please select it from the dropdown.'
                : raw;
            if (message) {
                dispatch(
                    setAlert({
                        message,
                        severity: 'error',
                    }),
                );
            } else {
                dispatch(
                    setAlert({
                        message:
                            'Unable to save service. Please try again or select an existing service from the dropdown.',
                        severity: 'error',
                    }),
                );
            }
        });
    };

    return (
        <HCModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add New Service"
            size="sm"
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <HCInput
                    label="Service Area"
                    name="serviceArea"
                    value={serviceArea}
                    onChange={(e) => setServiceArea(e.target.value)}
                    required
                    disabled={saving}
                    size="small"
                />
                <HCInput
                    label="Service Sub Area"
                    name="serviceSubArea"
                    value={serviceSubArea}
                    onChange={(e) => setServiceSubArea(e.target.value)}
                    required
                    disabled={saving}
                    size="small"
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        mt: 1,
                    }}
                >
                    <Button onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onSave}
                        disabled={saving}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </HCModal>
    );
};

export default AddFacilityServiceModal;
